import React, { useState } from 'react';
import { StatsCard } from './shared/StatsCard';
import { Chart } from './shared/Chart';
import { Download, DollarSign, Droplet, Fish, Wallet, AlertCircle, Coins, CreditCard, Star, TrendingUp, Zap, Gift, Calendar, Clock } from 'lucide-react';
// User segments
const segments = [{
  id: 'whales',
  label: 'Whales',
  tokenPurchases: 1250000,
  tokenSpends: 980000,
  subscriptions: 875,
  subscriptionRevenue: 43750,
  tokenRevenue: 125000,
  icon: <DollarSign size={16} className="text-emerald-500" data-id="element-14596" />
}, {
  id: 'dolphins',
  label: 'Dolphins',
  tokenPurchases: 3200000,
  tokenSpends: 2850000,
  subscriptions: 4250,
  subscriptionRevenue: 127500,
  tokenRevenue: 320000,
  icon: <Droplet size={16} className="text-blue-500" data-id="element-14597" />
}, {
  id: 'minnows',
  label: 'Minnows',
  tokenPurchases: 1850000,
  tokenSpends: 1780000,
  subscriptions: 3200,
  subscriptionRevenue: 47950,
  tokenRevenue: 92500,
  icon: <Fish size={16} className="text-cyan-500" data-id="element-14598" />
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  tokenPurchases: 0,
  tokenSpends: 0,
  subscriptions: 0,
  subscriptionRevenue: 0,
  tokenRevenue: 0,
  icon: <Wallet size={16} className="text-gray-500" data-id="element-14599" />
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  tokenPurchases: 450000,
  tokenSpends: 420000,
  subscriptions: 1850,
  subscriptionRevenue: 27750,
  tokenRevenue: 22500,
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-14600" />
}];
// Token packs
const tokenPacks = [{
  id: 'small',
  name: 'Small Pack',
  tokens: 100,
  price: 9.99,
  discount: 0,
  algorithmCoverage: 65,
  fallbackMethod: 'broad',
  conversions: 12500,
  revenue: 124875
}, {
  id: 'medium',
  name: 'Medium Pack',
  tokens: 500,
  price: 39.99,
  discount: 20,
  algorithmCoverage: 78,
  fallbackMethod: 'lookalike',
  conversions: 8750,
  revenue: 349912.5
}, {
  id: 'large',
  name: 'Large Pack',
  tokens: 1000,
  price: 69.99,
  discount: 30,
  algorithmCoverage: 92,
  fallbackMethod: 'lookalike',
  conversions: 3500,
  revenue: 244965
}, {
  id: 'whale',
  name: 'Whale Pack',
  tokens: 5000,
  price: 299.99,
  discount: 40,
  algorithmCoverage: 95,
  fallbackMethod: 'lookalike',
  conversions: 1250,
  revenue: 374987.5
}];
// Subscription tiers
const subscriptionTiers = [{
  id: 'basic',
  name: 'Basic Fan',
  price: 9.99,
  benefits: ['Access to fan club', 'Daily tokens (50)', 'Basic chat'],
  subscribers: 5250,
  revenue: 52447.5,
  algorithmCoverage: 72
}, {
  id: 'premium',
  name: 'Premium Fan',
  price: 19.99,
  benefits: ['Everything in Basic', 'Premium chat', 'Weekly lootbox', 'Exclusive content'],
  subscribers: 3750,
  revenue: 74962.5,
  algorithmCoverage: 85
}, {
  id: 'vip',
  name: 'VIP Fan',
  price: 49.99,
  benefits: ['Everything in Premium', 'Priority chat', 'Daily lootbox', 'AR/VR access'],
  subscribers: 1250,
  revenue: 62487.5,
  algorithmCoverage: 90
}, {
  id: 'elite',
  name: 'Elite Fan',
  price: 99.99,
  benefits: ['Everything in VIP', 'Private sessions', 'Custom content', 'Physical gifts'],
  subscribers: 850,
  revenue: 84991.5,
  algorithmCoverage: 95
}];
// Chart data
const revenueBySegmentData = [{
  name: 'Whales',
  tokens: 125000,
  subscriptions: 43750
}, {
  name: 'Dolphins',
  tokens: 320000,
  subscriptions: 127500
}, {
  name: 'Minnows',
  tokens: 92500,
  subscriptions: 47950
}, {
  name: 'Churn Risks',
  tokens: 22500,
  subscriptions: 27750
}];
const tokenSpendData = [{
  name: 'Chat',
  value: 35
}, {
  name: 'Gifts',
  value: 25
}, {
  name: 'Premium Content',
  value: 20
}, {
  name: 'Rituals',
  value: 15
}, {
  name: 'Other',
  value: 5
}];
export const TokenEconomy: React.FC = () => {
  const [tokenPricing, setTokenPricing] = useState(tokenPacks);
  const [subscriptionPricing, setSubscriptionPricing] = useState(subscriptionTiers);
  const [streakMultiplier, setStreakMultiplier] = useState(1.5);
  const handleTokenPriceChange = (id: string, newPrice: number) => {
    setTokenPricing(tokenPricing.map(pack => pack.id === id ? {
      ...pack,
      price: newPrice
    } : pack));
  };
  const handleSubscriptionPriceChange = (id: string, newPrice: number) => {
    setSubscriptionPricing(subscriptionPricing.map(tier => tier.id === id ? {
      ...tier,
      price: newPrice
    } : tier));
  };
  const handleStreakMultiplierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStreakMultiplier(parseFloat(e.target.value));
  };
  const totalTokenRevenue = segments.reduce((sum, segment) => sum + segment.tokenRevenue, 0);
  const totalSubscriptionRevenue = segments.reduce((sum, segment) => sum + segment.subscriptionRevenue, 0);
  const totalTokensPurchased = segments.reduce((sum, segment) => sum + segment.tokenPurchases, 0);
  const totalTokensSpent = segments.reduce((sum, segment) => sum + segment.tokenSpends, 0);
  const totalSubscribers = segments.reduce((sum, segment) => sum + segment.subscriptions, 0);
  return <div data-id="element-14601">
      <div className="flex items-center justify-between mb-6" data-id="element-14602">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-14603">
          Token & Subscription Economy
        </h2>
        <div className="flex items-center gap-3" data-id="element-14604">
          <button className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-14605">
            <Download size={16} data-id="element-14606" />
            <span data-id="element-14607">Export Revenue Data</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-id="element-14608">
        <StatsCard title="Token Revenue" value={`$${totalTokenRevenue.toLocaleString()}`} change={15.3} changeLabel="vs last month" icon={<Coins size={16} className="text-amber-500" data-id="element-14610" />} className="bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20" data-id="element-14609" />
        <StatsCard title="Subscription Revenue" value={`$${totalSubscriptionRevenue.toLocaleString()}`} change={8.7} changeLabel="vs last month" icon={<CreditCard size={16} className="text-indigo-500" data-id="element-14612" />} className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20" data-id="element-14611" />
        <StatsCard title="Tokens Purchased" value={`${(totalTokensPurchased / 1000000).toFixed(1)}M`} change={12.2} changeLabel="vs last month" icon={<Gift size={16} className="text-pink-500" data-id="element-14614" />} data-id="element-14613" />
        <StatsCard title="Tokens Spent" value={`${(totalTokensSpent / 1000000).toFixed(1)}M`} change={14.5} changeLabel="vs last month" icon={<Zap size={16} className="text-orange-500" data-id="element-14616" />} data-id="element-14615" />
        <StatsCard title="Subscribers" value={totalSubscribers.toLocaleString()} change={5.8} changeLabel="vs last month" icon={<Star size={16} className="text-purple-500" data-id="element-14618" />} data-id="element-14617" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-14619">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14620">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14621">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14622">
              <TrendingUp size={18} className="text-blue-500 mr-2" data-id="element-14623" />
              Revenue by Segment
            </h3>
          </div>
          <div className="p-6" data-id="element-14624">
            <div className="h-80" data-id="element-14625">
              <Chart title="" data={revenueBySegmentData} type="bar" height={300} yAxisLabel="Revenue ($)" data-id="element-14626" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14627">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14628">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14629">
              <Coins size={18} className="text-amber-500 mr-2" data-id="element-14630" />
              Token Spend Distribution
            </h3>
          </div>
          <div className="p-6" data-id="element-14631">
            <div className="h-80" data-id="element-14632">
              <Chart title="" data={tokenSpendData} type="pie" height={300} data-id="element-14633" />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-14634">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14635">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14636">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14637">
              <Coins size={18} className="text-amber-500 mr-2" data-id="element-14638" />
              Token Packages Configuration
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-14639">
            <table className="w-full" data-id="element-14640">
              <thead data-id="element-14641">
                <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-14642">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14643">
                    Package
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14644">
                    Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14645">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14646">
                    Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14647">
                    Algorithm Coverage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14648">
                    Fallback Method
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-14649">
                {tokenPricing.map(pack => <tr key={pack.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-14650">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-14651">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14652">
                        {pack.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14653">
                      {pack.tokens.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14654">
                      <input type="number" min="0.99" step="0.01" value={pack.price} onChange={e => handleTokenPriceChange(pack.id, parseFloat(e.target.value))} className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-14655" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14656">
                      {pack.discount > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400" data-id="element-14657">
                          {pack.discount}% off
                        </span> : <span data-id="element-14658">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14659">
                      <div className="flex items-center" data-id="element-14660">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-14661">
                          <div className="h-full bg-purple-500 rounded-full" style={{
                        width: `${pack.algorithmCoverage}%`
                      }} data-id="element-14662"></div>
                        </div>
                        <span className="ml-2" data-id="element-14663">{pack.algorithmCoverage}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14664">
                      <select defaultValue={pack.fallbackMethod} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-14665">
                        <option value="lookalike" data-id="element-14666">Lookalike Audiences</option>
                        <option value="broad" data-id="element-14667">Broad Targeting</option>
                        <option value="context" data-id="element-14668">Context Fallback</option>
                        <option value="persona" data-id="element-14669">Persona Fallback</option>
                      </select>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14670">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14671">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14672">
              <CreditCard size={18} className="text-indigo-500 mr-2" data-id="element-14673" />
              Subscription Tiers Configuration
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-14674">
            <table className="w-full" data-id="element-14675">
              <thead data-id="element-14676">
                <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-14677">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14678">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14679">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14680">
                    Subscribers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14681">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14682">
                    Algorithm Coverage
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-14683">
                {subscriptionPricing.map(tier => <tr key={tier.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-14684">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-14685">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14686">
                        {tier.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-14687">
                        {tier.benefits.join(' • ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14688">
                      <input type="number" min="0.99" step="0.01" value={tier.price} onChange={e => handleSubscriptionPriceChange(tier.id, parseFloat(e.target.value))} className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-14689" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14690">
                      {tier.subscribers.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14691">
                      ${tier.revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14692">
                      <div className="flex items-center" data-id="element-14693">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-14694">
                          <div className="h-full bg-purple-500 rounded-full" style={{
                        width: `${tier.algorithmCoverage}%`
                      }} data-id="element-14695"></div>
                        </div>
                        <span className="ml-2" data-id="element-14696">{tier.algorithmCoverage}%</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14697">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14698">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14699">
            <Calendar size={18} className="text-green-500 mr-2" data-id="element-14700" />
            Streak & Loyalty Multipliers
          </h3>
        </div>
        <div className="p-6" data-id="element-14701">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-14702">
            <div data-id="element-14703">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-14704">
                Login Streak Token Multiplier
              </label>
              <div className="flex items-center" data-id="element-14705">
                <input type="range" min="1" max="3" step="0.1" value={streakMultiplier} onChange={handleStreakMultiplierChange} className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-14706" />
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 w-12" data-id="element-14707">
                  {streakMultiplier}x
                </span>
              </div>
              <div className="mt-4 bg-gray-50 dark:bg-gray-900/30 p-4 rounded-lg" data-id="element-14708">
                <div className="flex items-center justify-between mb-2" data-id="element-14709">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-14710">
                    Login Day
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-14711">
                    Token Bonus
                  </div>
                </div>
                <div className="space-y-2" data-id="element-14712">
                  {[1, 3, 5, 7, 14, 30].map(day => <div key={day} className="flex items-center justify-between" data-id="element-14713">
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-14714">
                        Day {day}
                      </div>
                      <div className="text-sm text-green-600 dark:text-green-400 font-medium" data-id="element-14715">
                        +{Math.round(day * (streakMultiplier - 1) * 10)} tokens
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
            <div data-id="element-14716">
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg mb-4" data-id="element-14717">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14718">
                  <Clock size={16} className="text-amber-500 mr-2" data-id="element-14719" />
                  Loyalty Rewards
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-14720">
                  Configure loyalty rewards for long-term subscribers and token
                  purchasers.
                </p>
                <div className="space-y-3" data-id="element-14721">
                  <div className="flex items-center justify-between" data-id="element-14722">
                    <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14723">
                      1 Month Subscription
                    </div>
                    <div className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-id="element-14724">
                      +100 tokens
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-14725">
                    <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14726">
                      3 Month Subscription
                    </div>
                    <div className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-id="element-14727">
                      +500 tokens
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-14728">
                    <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14729">
                      6 Month Subscription
                    </div>
                    <div className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-id="element-14730">
                      +1,500 tokens
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-14731">
                    <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14732">
                      1 Year Subscription
                    </div>
                    <div className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-id="element-14733">
                      +5,000 tokens
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg" data-id="element-14734">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14735">
                  <Zap size={16} className="text-indigo-500 mr-2" data-id="element-14736" />
                  Fallback Targeting for Token Packs
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14737">
                  When algorithm confidence is below threshold, the system falls
                  back to:
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-14738">
                  <li className="flex items-center" data-id="element-14739">
                    <span className="w-3 h-3 bg-emerald-500 rounded-full mr-2" data-id="element-14740"></span>
                    <span data-id="element-14741">Whales: Lookalike targeting (95% confidence)</span>
                  </li>
                  <li className="flex items-center" data-id="element-14742">
                    <span className="w-3 h-3 bg-blue-500 rounded-full mr-2" data-id="element-14743"></span>
                    <span data-id="element-14744">Dolphins: Lookalike targeting (85% confidence)</span>
                  </li>
                  <li className="flex items-center" data-id="element-14745">
                    <span className="w-3 h-3 bg-cyan-500 rounded-full mr-2" data-id="element-14746"></span>
                    <span data-id="element-14747">
                      Minnows: Broad targeting with context (70% confidence)
                    </span>
                  </li>
                  <li className="flex items-center" data-id="element-14748">
                    <span className="w-3 h-3 bg-red-500 rounded-full mr-2" data-id="element-14749"></span>
                    <span data-id="element-14750">
                      Churn Risk: Persona-based targeting (60% confidence)
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};